/**
 * WebSocket client for multiplayer game communication
 */

export interface PlayerInfo {
  user_id: number;
  nickname: string;
  color: string;
  ready: boolean;
  is_host: boolean;
}

export interface PlayerPosition {
  user_id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  distance: number;
}

export interface GameResult {
  user_id: number;
  nickname: string;
  color: string;
  placement: number;
  final_distance: number;
  collected_bonuses: number;
  multiplier: number;
  payout: number;
  finish_reason: string;
}

export interface WSMessage {
  type: string;
  [key: string]: any;
}

type MessageHandler = (message: WSMessage) => void;

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private handlers: Map<string, MessageHandler[]> = new Map();
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;
  private pingInterval: ReturnType<typeof setInterval> | null = null;

  private roomId: number | null = null;
  private token: string = '';

  // Connection state
  public connected: boolean = false;
  public connecting: boolean = false;

  constructor() {}

  /**
   * Connect to a game room
   */
  async connect(roomId: number, token: string): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.disconnect();
    }

    this.roomId = roomId;
    this.token = token;
    this.connecting = true;

    return new Promise((resolve, reject) => {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = import.meta.env.VITE_WS_URL || `${wsProtocol}//${window.location.host}`;
      const url = `${wsHost}/ws/game/${roomId}?token=${token}`;

      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log('[WS] Connected to room', roomId);
        this.connected = true;
        this.connecting = false;
        this.reconnectAttempts = 0;
        this.startPing();
        resolve();
      };

      this.ws.onclose = (event) => {
        console.log('[WS] Disconnected', event.code, event.reason);
        this.connected = false;
        this.connecting = false;
        this.stopPing();

        this.emit({ type: 'disconnected', code: event.code, reason: event.reason });

        // Auto-reconnect if not intentional disconnect
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WS] Error:', error);
        this.connecting = false;
        reject(error);
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WSMessage;
          this.emit(message);
        } catch (e) {
          console.error('[WS] Failed to parse message:', e);
        }
      };
    });
  }

  /**
   * Disconnect from the current room
   */
  disconnect(): void {
    this.stopPing();
    if (this.ws) {
      this.ws.close(1000, 'User disconnect');
      this.ws = null;
    }
    this.connected = false;
    this.connecting = false;
    this.roomId = null;
  }

  /**
   * Send a message to the server
   */
  send(message: WSMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[WS] Cannot send - not connected');
      return;
    }
    this.ws.send(JSON.stringify(message));
  }

  /**
   * Register a handler for a specific message type
   */
  on(type: string, handler: MessageHandler): void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, []);
    }
    this.handlers.get(type)!.push(handler);
  }

  /**
   * Remove a handler for a specific message type
   */
  off(type: string, handler: MessageHandler): void {
    const handlers = this.handlers.get(type);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Remove all handlers
   */
  clearHandlers(): void {
    this.handlers.clear();
  }

  /**
   * Emit a message to all registered handlers
   */
  private emit(message: WSMessage): void {
    const handlers = this.handlers.get(message.type);
    if (handlers) {
      handlers.forEach(handler => handler(message));
    }

    // Also emit to wildcard handlers
    const wildcardHandlers = this.handlers.get('*');
    if (wildcardHandlers) {
      wildcardHandlers.forEach(handler => handler(message));
    }
  }

  /**
   * Start ping interval to keep connection alive
   */
  private startPing(): void {
    this.pingInterval = setInterval(() => {
      if (this.connected) {
        this.send({ type: 'ping', timestamp: Date.now() });
      }
    }, 30000);
  }

  /**
   * Stop ping interval
   */
  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Schedule a reconnection attempt
   */
  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`[WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      if (this.roomId && this.token) {
        this.connect(this.roomId, this.token).catch(console.error);
      }
    }, delay);
  }

  // ========== Game Commands ==========

  /**
   * Set ready status
   */
  setReady(ready: boolean): void {
    this.send({ type: 'ready', ready });
  }

  /**
   * Start the game (host only)
   */
  startGame(): void {
    this.send({ type: 'start_game' });
  }

  /**
   * Send position update
   */
  sendPosition(x: number, y: number, vx: number, vy: number, fuel: number, distance: number): void {
    this.send({
      type: 'position_update',
      x, y, vx, vy, fuel, distance
    });
  }

  /**
   * Collect a bonus
   */
  collectBonus(bonusIndex: number): void {
    this.send({ type: 'collect_bonus', bonus_index: bonusIndex });
  }

  /**
   * Notify that player finished (crashed/landed/cashed out)
   */
  playerFinished(reason: string, distance: number): void {
    this.send({ type: 'player_finished', reason, distance });
  }

  /**
   * Request rematch (host only)
   */
  requestRematch(): void {
    this.send({ type: 'rematch' });
  }
}

// Singleton instance
export const wsClient = new WebSocketClient();
